import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { createPaymentRecord } from './payments';
import {
  allocateRegistrationNumber,
  ensureUserRegistrationNumber,
} from './registrationNumbers';
import { notifyAdminStudentEnrolled, notifyStudentEnrolled } from './notifications';

/**
 * Payment history is not stored on the user profile.
 * Hosted bank checkout keeps order data in the payments collection only.
 */
export async function recordPayment() {
  return null;
}

/** @deprecated use SUBSCRIPTION_PLANS */
export const SUBSCRIPTION_DAYS = 30;

export const SUBSCRIPTION_PLANS = {
  '1-month': {
    id: '1-month',
    label: '1 Month GED Study',
    days: 30,
  },
  '2-month': {
    id: '2-month',
    label: '2 Month GED Study',
    days: 60,
  },
  '3-month': {
    id: '3-month',
    label: '3 Month GED Study',
    days: 90,
  },
};

export function getPlanById(planId) {
  return SUBSCRIPTION_PLANS[planId] || SUBSCRIPTION_PLANS['1-month'];
}

export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function normalizeEmail(email) {
  return (email || '').trim().toLowerCase();
}

export function getPlanDays(sub) {
  if (sub?.planDays) return sub.planDays;
  if (sub?.planId === '3-month') return 90;
  if (sub?.planId === '2-month') return 60;
  return SUBSCRIPTION_DAYS;
}

export function isSubscriptionActive(sub) {
  if (!sub || sub.status !== 'active') return false;
  const end = sub.endDate?.toDate ? sub.endDate.toDate() : new Date(sub.endDate);
  return end > new Date();
}

export function getDaysRemaining(sub) {
  if (!sub?.endDate) return 0;
  const end = sub.endDate?.toDate ? sub.endDate.toDate() : new Date(sub.endDate);
  const ms = end.getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

async function fetchSubscriptionsByEmail(email, status = 'active') {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return [];

  const snap = await getDocs(
    query(
      collection(db, 'subscriptions'),
      where('email', '==', normalizedEmail),
      where('status', '==', status)
    )
  );

  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function fetchSubscriptionsByUserId(userId, status = 'active') {
  if (!userId) return [];

  const snap = await getDocs(
    query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      where('status', '==', status)
    )
  );

  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Returns the single active subscription for an email, if any. */
export async function findActiveSubscriptionByEmail(email) {
  const subs = (await fetchSubscriptionsByEmail(email)).filter(isSubscriptionActive);
  return subs[0] || null;
}

/** Returns the single active subscription for a Firebase uid, if any. */
export async function findActiveSubscriptionByUserId(userId) {
  const subs = (await fetchSubscriptionsByUserId(userId)).filter(isSubscriptionActive);
  return subs[0] || null;
}

export class SubscriptionExistsError extends Error {
  constructor(existing) {
    super('This account already has an active subscription.');
    this.name = 'SubscriptionExistsError';
    this.existing = existing;
  }
}

/** Ensures a student does not already have an active subscription. */
export async function assertNoActiveSubscription({ email, userId = '' }) {
  const byEmail = await findActiveSubscriptionByEmail(email);
  if (byEmail) throw new SubscriptionExistsError(byEmail);

  const resolvedUserId = userId || (await findUserIdByEmail(email));
  if (resolvedUserId) {
    const byUid = await findActiveSubscriptionByUserId(resolvedUserId);
    if (byUid) throw new SubscriptionExistsError(byUid);
  }
}

async function fetchActiveSubscriptions(user) {
  const email = normalizeEmail(user.email);
  const [byUid, byEmail] = await Promise.all([
    getDocs(
      query(
        collection(db, 'subscriptions'),
        where('userId', '==', user.uid),
        where('status', '==', 'active')
      )
    ),
    email
      ? getDocs(
          query(
            collection(db, 'subscriptions'),
            where('email', '==', email),
            where('status', '==', 'active')
          )
        )
      : Promise.resolve({ docs: [] }),
  ]);

  const seen = new Set();
  return [...byUid.docs, ...byEmail.docs]
    .filter((d) => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    })
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((sub) => isSubscriptionActive(sub));
}

/** Link email-based subscriptions to Firebase uid (non-fatal if rules block). */
async function tryLinkSubscriptionToUser(email, userId) {
  if (!email || !userId) return;
  try {
    await linkSubscriptionToUser(email, userId);
  } catch (err) {
    console.warn('Subscription link skipped:', err?.message || err);
  }
}

/**
 * Returns active subscription summary for display (days left, total plan days).
 */
export async function getActiveSubscription(user, isAdmin) {
  if (!user) return null;
  if (isAdmin) {
    return {
      isAdmin: true,
      daysLeft: SUBSCRIPTION_DAYS,
      totalDays: SUBSCRIPTION_DAYS,
      planLabel: 'Admin access',
      planId: 'admin',
    };
  }

  const subs = await fetchActiveSubscriptions(user);
  if (subs.length === 0) {
    // Fallback: mock payment unlock on user profile
    try {
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      if (userSnap.exists()) {
        const data = userSnap.data();
        if (data.accessStatus === 'active' && data.subscriptionEndAt) {
          const end = data.subscriptionEndAt?.toDate?.()
            ? data.subscriptionEndAt.toDate()
            : new Date(data.subscriptionEndAt);
          if (end > new Date()) {
            const totalDays = Number(data.paidPlanDays) || SUBSCRIPTION_DAYS;
            const daysLeft = Math.max(0, Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
            return {
              isAdmin: false,
              daysLeft,
              totalDays,
              planLabel: data.paidPlanLabel || getPlanById(data.paidPlanId).label,
              planId: data.paidPlanId || '1-month',
              endDate: data.subscriptionEndAt,
              registrationNumber: data.registrationNumber || '',
              mockPayment: true,
            };
          }
        }
      }
    } catch (err) {
      console.warn('Profile subscription info skipped:', err?.message || err);
    }

    return {
      isAdmin: false,
      daysLeft: 0,
      totalDays: SUBSCRIPTION_DAYS,
      planLabel: 'No active subscription',
      planId: null,
    };
  }

  const sub = subs.sort((a, b) => {
    const endA = a.endDate?.toDate?.() || new Date(0);
    const endB = b.endDate?.toDate?.() || new Date(0);
    return endB - endA;
  })[0];

  const email = normalizeEmail(user.email);
  if (email) await tryLinkSubscriptionToUser(email, user.uid);

  const totalDays = getPlanDays(sub);
  return {
    isAdmin: false,
    daysLeft: getDaysRemaining(sub),
    totalDays,
    planLabel: sub.planLabel || getPlanById(sub.planId).label,
    planId: sub.planId || '1-month',
    endDate: sub.endDate,
    registrationNumber: sub.registrationNumber || '',
  };
}

/**
 * Check if user has an active subscription (by uid or email). Admins always pass.
 * Also honors mock-payment unlock fields on the user profile.
 */
export async function checkActiveSubscription(user, isAdmin) {
  if (!user) return false;
  if (isAdmin) return true;

  try {
    const userSnap = await getDoc(doc(db, 'users', user.uid));
    if (userSnap.exists()) {
      const data = userSnap.data();
      if (data.accessStatus === 'active' && data.subscriptionEndAt) {
        const end = data.subscriptionEndAt?.toDate?.()
          ? data.subscriptionEndAt.toDate()
          : new Date(data.subscriptionEndAt);
        if (end > new Date()) return true;
      }
    }
  } catch (err) {
    console.warn('Profile subscription check skipped:', err?.message || err);
  }

  const subs = await fetchActiveSubscriptions(user);
  const active = subs.length > 0;

  if (active && user.email) {
    await tryLinkSubscriptionToUser(user.email, user.uid);
  }

  return active;
}

/** Attach userId to email-based subscriptions when student signs in. */
export async function linkSubscriptionToUser(email, userId) {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !userId) return;

  const snap = await getDocs(
    query(collection(db, 'subscriptions'), where('email', '==', normalizedEmail))
  );

  const updates = snap.docs
    .filter((d) => !d.data().userId)
    .map((d) => updateDoc(doc(db, 'subscriptions', d.id), { userId }));

  await Promise.all(updates);
}

/**
 * Admin: create a subscription plan for a student.
 * Assigns a unique registration number and creates a payment/invoice record when payment info is present.
 */
export async function createSubscription({
  studentName,
  email,
  userId = '',
  planId = '1-month',
  planDays,
  planLabel,
  createdBy = '',
  payment = null, // { method: 'online'|'cash', date: 'YYYY-MM-DD', amount, packageTotal }
}) {
  const plan = getPlanById(planId);
  const days = Number(planDays) > 0 ? Number(planDays) : plan.days;
  const label = (planLabel || '').trim() || plan.label;
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) throw new Error('A valid student email is required.');

  await assertNoActiveSubscription({ email: normalizedEmail, userId });

  const resolvedUserId = userId || (await findUserIdByEmail(normalizedEmail)) || '';
  let registrationNumber = '';
  if (resolvedUserId) {
    registrationNumber = await ensureUserRegistrationNumber(resolvedUserId);
  } else {
    registrationNumber = await allocateRegistrationNumber();
  }

  const startDate = new Date();
  const endDate = addDays(startDate, days);

  const docData = {
    studentName: studentName.trim(),
    email: normalizedEmail,
    userId: resolvedUserId || '',
    registrationNumber,
    status: 'active',
    planId: planId || plan.id,
    planDays: days,
    startDate: Timestamp.fromDate(startDate),
    endDate: Timestamp.fromDate(endDate),
    planLabel: label,
    createdBy,
    createdAt: serverTimestamp(),
  };

  if (payment) {
    docData.paid = true;
  }

  const ref = await addDoc(collection(db, 'subscriptions'), docData);

  let paymentRecord = null;
  if (payment && (Number(payment.amount) > 0 || payment.createInvoice !== false)) {
    const amount = Number(payment.amount) || Number(payment.packageTotal) || 0;
    try {
      paymentRecord = await createPaymentRecord({
        userId: resolvedUserId || '',
        category: 'course',
        label,
        amount,
        currency: payment.currency || 'USD',
        breakdown: {
          items: [{ name: label, amount }],
          serviceAndTax: 0,
          subtotal: amount,
        },
        mode: payment.method === 'cash' ? 'cash' : 'hosted-bank',
        status: 'paid',
        paymentDate: payment.date ? new Date(payment.date) : new Date(),
        planId: planId || plan.id,
        days,
        createdBy: 'admin-enroll',
        createdByUid: createdBy || '',
      });
    } catch (err) {
      console.warn('Invoice payment record skipped:', err?.message || err);
    }
  }

  await notifyAdminStudentEnrolled({
    studentName: studentName.trim(),
    email: normalizedEmail,
    registrationNumber,
  }).catch(console.warn);

  return {
    id: ref.id,
    registrationNumber,
    paymentId: paymentRecord?.id || null,
    payment: paymentRecord,
  };
}

export async function revokeSubscription(subscriptionId) {
  await updateDoc(doc(db, 'subscriptions', subscriptionId), {
    status: 'revoked',
    revokedAt: serverTimestamp(),
  });
}

export async function extendSubscriptionById(subscriptionId, extraDays) {
  const ref = doc(db, 'subscriptions', subscriptionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Subscription not found');

  const data = snap.data();
  const currentEnd = data.endDate?.toDate?.() || new Date();
  const base = currentEnd > new Date() ? currentEnd : new Date();
  const newEnd = addDays(base, extraDays);

  await updateDoc(ref, {
    endDate: Timestamp.fromDate(newEnd),
    status: 'active',
    extendedAt: serverTimestamp(),
  });
}

export async function deleteSubscription(subscriptionId) {
  await deleteDoc(doc(db, 'subscriptions', subscriptionId));
}

export async function findUserIdByEmail(email) {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return null;

  const snap = await getDocs(
    query(collection(db, 'users'), where('emailLower', '==', normalizedEmail))
  );
  if (snap.empty) return null;
  return snap.docs[0].id;
}

/**
 * Admin: create subscription and attach uid when student account already exists.
 */
export async function createSubscriptionForStudent({
  studentName,
  email,
  planId = '1-month',
  planDays,
  planLabel,
  createdBy = '',
  payment = null,
}) {
  const userId = (await findUserIdByEmail(email)) || '';
  return createSubscription({
    studentName,
    email,
    userId,
    planId,
    planDays,
    planLabel,
    createdBy,
    payment,
  });
}

/**
 * Student self-serve mock payment → unlocks portal access.
 * Prefer writing subscription doc; always writes user profile unlock fields
 * (works even when subscription create is admin-only in Firestore rules).
 */
export async function activateMockPaymentSubscription({
  user,
  planId = '1-month',
  planDays,
  planLabel,
  amountPaid = 0,
  currency = 'USD',
}) {
  if (!user?.uid) throw new Error('You must be signed in to complete payment.');

  const plan = getPlanById(planId);
  const days = Number(planDays) > 0 ? Number(planDays) : plan.days;
  const label = (planLabel || '').trim() || plan.label;
  const normalizedEmail = normalizeEmail(user.email);

  const already = await checkActiveSubscription(user, false);
  if (already) throw new SubscriptionExistsError({ email: normalizedEmail, userId: user.uid });

  const startDate = new Date();
  const endDate = addDays(startDate, days);
  const registrationNumber = await ensureUserRegistrationNumber(user.uid);

  // Profile unlock (always allowed for own user doc)
  await updateDoc(doc(db, 'users', user.uid), {
    accessStatus: 'active',
    paidPlanId: planId || plan.id,
    paidPlanDays: days,
    paidPlanLabel: label,
    subscriptionEndAt: Timestamp.fromDate(endDate),
    registrationNumber,
  });

  // Entitlement only — no name, email, or card data on the subscription.
  try {
    await assertNoActiveSubscription({ email: normalizedEmail, userId: user.uid });
    await addDoc(collection(db, 'subscriptions'), {
      userId: user.uid,
      status: 'active',
      planId: planId || plan.id,
      planDays: days,
      startDate: Timestamp.fromDate(startDate),
      endDate: Timestamp.fromDate(endDate),
      planLabel: label,
      createdBy: 'hosted-bank',
      paid: true,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Subscription doc create skipped (profile unlock still applied):', err?.message || err);
  }

  const payment = await createPaymentRecord({
    userId: user.uid,
    category: 'course',
    label,
    amount: amountPaid,
    currency,
    breakdown: {
      items: [{ name: label, amount: Number(amountPaid) || 0 }],
      serviceAndTax: 0,
      subtotal: Number(amountPaid) || 0,
    },
    mode: 'hosted-bank',
    status: 'paid',
    planId: planId || plan.id,
    days,
    createdBy: 'student-hosted',
    createdByUid: user.uid,
  });

  await notifyAdminStudentEnrolled({
    studentName: '',
    email: '',
    registrationNumber,
  }).catch(console.warn);
  await notifyStudentEnrolled({
    userId: user.uid,
    registrationNumber,
    planLabel: label,
  }).catch(console.warn);

  return {
    userId: user.uid,
    planId: plan.id,
    endDate,
    registrationNumber,
    paymentId: payment.id,
    payment,
  };
}

/**
 * Student self-serve "need more days" extension (mock payment).
 * Extends the profile-based subscription end date; records the payment.
 */
export async function extendOwnSubscription({ user, days, price = 0, currency = 'USD' }) {
  if (!user?.uid) throw new Error('You must be signed in.');
  const extraDays = Math.max(1, Math.round(Number(days) || 0));

  const userRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userRef);
  const data = snap.exists() ? snap.data() : {};

  // Base = later of (now, current profile end date, active subscription doc end date)
  let base = new Date();
  const profileEnd = data.subscriptionEndAt?.toDate?.()
    ? data.subscriptionEndAt.toDate()
    : data.subscriptionEndAt
      ? new Date(data.subscriptionEndAt)
      : null;
  if (profileEnd && profileEnd > base) base = profileEnd;

  try {
    const subs = await fetchActiveSubscriptions(user);
    for (const sub of subs) {
      const end = sub.endDate?.toDate?.() ? sub.endDate.toDate() : new Date(sub.endDate);
      if (end > base) base = end;
    }
  } catch (err) {
    console.warn('Extension base check skipped:', err?.message || err);
  }

  const newEnd = addDays(base, extraDays);

  await updateDoc(userRef, {
    accessStatus: 'active',
    subscriptionEndAt: Timestamp.fromDate(newEnd),
    paidPlanDays: (Number(data.paidPlanDays) || 0) + extraDays,
    lastExtensionDays: extraDays,
    extendedAt: serverTimestamp(),
  });

  const label = `${extraDays} extra days`;
  const payment = await createPaymentRecord({
    userId: user.uid,
    category: 'extension',
    label,
    amount: price,
    currency,
    breakdown: {
      items: [{ name: label, amount: Number(price) || 0 }],
      serviceAndTax: 0,
      subtotal: Number(price) || 0,
    },
    mode: 'hosted-bank',
    status: 'paid',
    days: extraDays,
    createdBy: 'student-hosted',
    createdByUid: user.uid,
  });

  return { newEnd, extraDays, paymentId: payment.id, payment };
}
